import React, { useState, useEffect, useRef } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import DefaultEditor from "react-simple-wysiwyg";
import axios from "axios";
import api from "../../services/api";
import CommonSelect from "../../core/common/commonSelect";
import ImageSelectMultiple from "../common/imageSelectMultiple";
import { showBasicAlert } from "../../core/common/useAlert";
import socketService from "../../services/socketService";
import { getEmployeeImageUrl } from "../../feature-module/application/todo/services/todoCommanService";
import "../../style/css/todo.css";
import "../../style/css/taskViewModal.css";
import { Modal } from "bootstrap";

// Type Definitions
interface Company {
  id: string;
  name: string;
  type: string;
}

interface User {
  EmpId: string;
  Name: string;
  Email: string;
  CompanyId?: number | string;
  CompanyType?: string;
  CompanyName?: string;
  avatar?: string;
  Image?: string;
  Phone_Number: string;
  Phone_code: string;
  Official_Phone_Number: string;
  Official_Phone_code: string;
}

interface FilePreview {
  name: string;
  url: string;
  type: string;
}

interface Comment {
  id: number;
  user_id: number;
  comment: string;
  todo_id: number;
  company_id: number;
  company_type: string;
  attachments: FilePreview[];
  reporter: any;
  createdAt: string;
  updatedAt: string;
}

interface Todo {
  id?: string;
  signature: string | null;
  title: string;
  description: string;
  priority: number;
  stage: number;
  status: number;
  company_id: string;
  company_name: string;
  company_type: string;
  due_date: string;
  recurring_date?: string | null;
  recurring_type?: string | null;
  tags: string[];
  attachments: any[];
  reporter?: Record<string, any>;
  assigned_members: Array<Record<string, any>>;
  team_leads: Array<Record<string, any>>;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface TodoModalProps {
  company?: Company;
  todo?: Todo | null;
  viewTodo?: Todo | null;
  onSave?: (newTodo?: Todo) => void;
}

const TodoModal: React.FC<TodoModalProps> = ({
  company,
  todo,
  viewTodo,
  onSave,
}) => {
  const signature = localStorage.getItem("signature");
  const [formData, setFormData] = useState<Todo>({
    signature,
    title: "",
    description: "",
    priority: 0,
    stage: 0,
    status: 1,
    company_id: company?.id || "",
    company_name: company?.name || "",
    company_type: company?.type || "",
    due_date: "",
    recurring_date: null,
    tags: [],
    attachments: [],
    assigned_members: [],
    team_leads: [],
    isActive: true,
  });
  const [uiTodoType, setUiTodoType] = useState<"Normal" | "Recurring">(
    "Normal"
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    company?.id || null
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);
  const [allData, setAllData] = useState<{
    companies: Company[];
    users: User[];
  }>({
    companies: [],
    users: [],
  });
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState<string>("");
  const [commentFilePreviews, setCommentFilePreviews] = useState<FilePreview[]>(
    []
  );
  const [isCommentLoading, setIsCommentLoading] = useState<boolean>(false);
  const [dueDateError, setDueDateError] = useState<string | null>(null);

  const [selectedAssignedMembers, setSelectedAssignedMembers] = useState<any[]>(
    []
  );

  const [selectedTeamLeads, setSelectedTeamLeads] = useState<any[]>([]);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState<string>("");
  const [isEditingComment, setIsEditingComment] = useState<boolean>(false);
  const [isFormSubmitSuccess, setIsFormSubmitSuccess] = useState(false);
  const submitBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (todo && isEditModalOpen) {
      setFileUploadError(null);
      const mappedAssigned = todo.assigned_members.map((member) => ({
        label: member.name,
        value: member.id,
        imageUrl: member.avatar,
      }));
      setSelectedAssignedMembers(mappedAssigned);

      const mappedFollowers = todo.team_leads.map((member) => ({
        label: member.name,
        value: member.id,
        imageUrl: member.avatar,
      }));
      setSelectedTeamLeads(mappedFollowers);
    }
  }, [todo, isEditModalOpen]);

  useEffect(() => {
    if (uiTodoType === "Normal") {
      setFormData((prev) => ({
        ...prev,
        recurring_type: null,
        recurring_date: null,
      }));
    }
  }, [uiTodoType]);

  const getWeekDays = () => [
    { value: "1", label: "Monday" },
    { value: "2", label: "Tuesday" },
    { value: "3", label: "Wednesday" },
    { value: "4", label: "Thursday" },
    { value: "5", label: "Friday" },
    { value: "6", label: "Saturday" },
    { value: "0", label: "Sunday" },
  ];

  const getMonthDays = () => {
    const days = [];
    for (let i = 1; i <= 30; i++) {
      days.push({
        value: i.toString(),
        label:
          i.toString() +
          (i === 1 ? "st" : i === 2 ? "nd" : i === 3 ? "rd" : "th"),
      });
    }
    return days;
  };

  const [recurringOptions, setRecurringOptions] = useState<any[]>(
    getWeekDays()
  );

  const recurringType = [
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Quarterly", label: "Quarterly" },
  ];

  const getQuarterMonths = () => [
    { value: "1", label: "January - March" },
    { value: "2", label: "April - June" },
    { value: "3", label: "July - September" },
    { value: "4", label: "October - December" },
  ];

  const [selectedQuarter, setSelectedQuarter] = useState<any>(null);
  const [selectedQuarterMonth, setSelectedQuarterMonth] = useState<any>(null);
  const [showMonthSelection, setShowMonthSelection] = useState(false);
  const [showDaySelection, setShowDaySelection] = useState(false);
  const [selectedRecurringOption, setSelectedRecurringOption] =
    useState<any>(null);
  const [selectedDueDateOption, setSelectedDueDateOption] = useState<any>(null);

  const handleRecurringTypeChange = (selected: any) => {
    const newRecurringType = selected?.value;
    setFormData((prev) => ({
      ...prev,
      recurring_type: newRecurringType,
      recurring_date: null,
      recurring_day: null,
      recurring_month: null,
      recurring_quarter: null,
    }));

    if (newRecurringType === "Weekly") {
      setRecurringOptions(getWeekDays());
      setShowMonthSelection(false);
      setShowDaySelection(false);
    } else if (newRecurringType === "Monthly") {
      setRecurringOptions(getMonthDays());
      setShowMonthSelection(false);
      setShowDaySelection(false);
    } else if (newRecurringType === "Quarterly") {
      setRecurringOptions(getQuarterMonths());
      setShowMonthSelection(false);
      setShowDaySelection(false);
    }

    setSelectedRecurringOption(null);
    setSelectedQuarter(null);
    setSelectedQuarterMonth(null);
  };

  const handleQuarterSelect = (selected: any) => {
    setSelectedQuarter(selected);
    setSelectedQuarterMonth(null);
    setSelectedRecurringOption(null);
    setFormData((prev) => ({
      ...prev,
      recurring_date: null,
      recurring_quarter: selected.value,
      recurring_month: null,
      recurring_day: null,
    }));
  };

  const handleMonthInQuarterSelect = (selected: any) => {
    setSelectedQuarterMonth(selected);
    setSelectedRecurringOption(null);
    setFormData((prev) => ({
      ...prev,
      recurring_date: null,
      recurring_month: selected.value,
      recurring_day: null,
    }));
  };

  const getQuarterMonthOptions = (quarterValue: any) => {
    const quarter = parseInt(quarterValue);
    const monthOptions = [];

    for (let i = 1; i <= 3; i++) {
      const monthNumber = (quarter - 1) * 3 + i;
      const monthName = new Date(0, monthNumber - 1).toLocaleString("default", {
        month: "long",
      });
      monthOptions.push({
        value: monthNumber.toString(),
        label: `${i}${
          i === 1 ? "st" : i === 2 ? "nd" : "rd"
        } month (${monthName})`,
      });
    }

    return monthOptions;
  };

  const handleRecurringOptionSelect = (selected: any) => {
    setSelectedRecurringOption(selected);

    let nextDate = "";
    const today = new Date();

    if (formData.recurring_type === "Weekly") {
      const selectedDay = parseInt(selected.value);
      const currentDay = today.getDay();
      let daysToAdd = (selectedDay - currentDay + 7) % 7;
      daysToAdd = daysToAdd === 0 ? 7 : daysToAdd;

      const nextDateObj = new Date(today);
      nextDateObj.setDate(today.getDate() + daysToAdd);
      nextDate = nextDateObj.toISOString().split("T")[0];

      const dueDateObj = new Date(nextDateObj);
      dueDateObj.setDate(dueDateObj.getDate() + 7);
      const dueDate = dueDateObj.toISOString().split("T")[0];

      setFormData((prev) => ({
        ...prev,
        recurring_date: nextDate,
        due_date: dueDate,
        recurring_day: selected.value,
      }));
    } else if (formData.recurring_type === "Monthly") {
      const dayOfMonth = parseInt(selected.value) + 1;
      const nextDateObj = new Date(
        today.getFullYear(),
        today.getMonth(),
        dayOfMonth
      );

      if (nextDateObj < today) {
        nextDateObj.setMonth(nextDateObj.getMonth() + 1);
      }

      const daysInMonth = new Date(
        nextDateObj.getFullYear(),
        nextDateObj.getMonth() + 1,
        0
      ).getDate();

      nextDateObj.setDate(Math.min(dayOfMonth, daysInMonth));
      nextDate = nextDateObj.toISOString().split("T")[0];

      setFormData((prev) => ({
        ...prev,
        recurring_date: nextDate,
        recurring_day: selected.value,
      }));
    } else if (formData.recurring_type === "Quarterly") {
      const month = parseInt(selectedQuarterMonth.value) - 1;
      let day = parseInt(selected.value) + 1;

      if (month === 1) {
        const year = today.getFullYear();
        const isLeapYear =
          (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        const maxFebDay = isLeapYear ? 30 : 29;
        day = Math.min(day, maxFebDay);
      }

      let nextDateObj = new Date(today.getFullYear(), month, day);

      if (month !== 1) {
        const daysInMonth = new Date(
          nextDateObj.getFullYear(),
          nextDateObj.getMonth() + 1,
          0
        ).getDate();
        day = Math.min(day, daysInMonth);
        nextDateObj.setDate(day);
      }

      const nextDate = nextDateObj.toISOString().split("T")[0];

      const nextMonth = nextDateObj.getMonth();
      const quarter = Math.floor(nextMonth / 3);
      const endMonth = (quarter + 1) * 3;
      const dueDateObj = new Date(nextDateObj.getFullYear(), endMonth, 0);
      const dueDate = dueDateObj.toISOString().split("T")[0];

      setFormData((prev) => ({
        ...prev,
        recurring_date: nextDate,
        due_date: dueDate,
        recurring_day: selected.value,
      }));
    }
  };

  const handleDueDateMonthly = (selected: any) => {
    setSelectedDueDateOption(selected);

    let nextDate = "";
    const today = new Date();

    if (formData.recurring_type === "Monthly") {
      const dayOfMonth = parseInt(selected.value) + 1;
      const nextDateObj = new Date(
        today.getFullYear(),
        today.getMonth(),
        dayOfMonth
      );

      if (nextDateObj < today) {
        nextDateObj.setMonth(nextDateObj.getMonth() + 1);
      }

      const daysInMonth = new Date(
        nextDateObj.getFullYear(),
        nextDateObj.getMonth() + 1,
        0
      ).getDate();

      nextDateObj.setDate(Math.min(dayOfMonth, daysInMonth));
      nextDate = nextDateObj.toISOString().split("T")[0];
      setFormData((prev) => ({
        ...prev,
        due_date: nextDate,
      }));
    }
  };

  const fallbackHTML = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const elements = doc.querySelectorAll("*");
    elements.forEach((el) => {
      const style = el.getAttribute("style") || "";
      const newStyle = style
        .replace(/var\(--bs-modal-color\)/g, "#212529")
        .replace(/var\(--bs-modal-bg\)/g, "#ffffff")
        .replace(/var\(--bs-body-text-align\)/g, "left")
        .replace(/var\(--bs-body-font-family\)/g, "inherit")
        .replace(/var\(--bs-body-font-size\)/g, "1rem");
      el.setAttribute("style", newStyle);
    });

    const lists = doc.querySelectorAll("ul, ol");
    lists.forEach((list) => {
      if (list.tagName === "UL") {
        list.setAttribute(
          "style",
          "list-style-type: disc; margin-left: 1.5em; padding-left: 1.5em;"
        );
      } else if (list.tagName === "OL") {
        list.setAttribute(
          "style",
          "list-style-type: decimal; margin-left: 1.5em; padding-left: 1.5em;"
        );
      }
    });

    if (doc.body.children.length === 0) {
      return html;
    }

    return doc.body.innerHTML;
  };

  const priorityOptions = [
    { value: "0", label: "High" },
    { value: "1", label: "Medium" },
    { value: "2", label: "Low" },
  ];

  const stageOptions = [
    { value: "0", label: "Pending" },
    { value: "1", label: "Inprogress" },
    { value: "2", label: "Onhold" },
    { value: "3", label: "Completed" },
  ];

  const tagOptions = [
    { value: "Internal", label: "Internal" },
    { value: "Projects", label: "Projects" },
    { value: "Meetings", label: "Meetings" },
    { value: "Reminder", label: "Reminder" },
    { value: "Research", label: "Research" },
  ];

  const todoTypeOptions = [
    { value: "Normal", label: "Normal" },
    { value: "Recurring", label: "Recurring" },
  ];

  const handleStopRecurring = async () => {
    if (!formData.id) return;

    setIsLoading(true);
    try {
      const response = await api.post("todoService/stopRecurringTodo", {
        signature,
        id: formData.id,
      });

      showBasicAlert(response.data.message[0].msg);

      if (onSave) onSave();
    } catch (error: any) {
      showBasicAlert(
        error?.data?.message?.[0]?.msg || "Failed to stop recurrence"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (viewTodo?.id && isViewModalOpen) {
      fetchComments();
    }
  }, [viewTodo?.id, isViewModalOpen]);

  useEffect(() => {
    if (todo && isEditModalOpen) {
      const normalizedAttachments = Array.isArray(todo.attachments)
        ? todo.attachments.map((att: any) => ({
            name: att.name || att.fileName || "Unknown",
            url: att.url || att.fileUrl || "",
            type: att.type || att.fileType || "application/octet-stream",
          }))
        : [];
      setFormData({
        signature,
        title: todo.title || "",
        description: todo.description || "",
        priority: todo.priority ?? 0,
        stage: todo.stage ?? 0,
        status: todo.status ?? 1,
        company_id: todo.company_id || company?.id || "",
        company_name: todo.company_name || company?.name || "",
        company_type: todo.company_type || company?.type || "",
        due_date: todo.due_date || "",
        recurring_date: todo.recurring_date || null,
        recurring_type: todo.recurring_type || null,
        tags: todo.tags || [],
        attachments: normalizedAttachments,
        assigned_members: todo.assigned_members || [],
        team_leads: todo.team_leads || [],
        isActive: todo.isActive ?? true,
        id: todo.id,
        createdAt: todo.createdAt,
        updatedAt: todo.updatedAt,
      });
      setUiTodoType(todo.recurring_date ? "Recurring" : "Normal");
      setFilePreviews(normalizedAttachments);
      setSelectedCompanyId(todo.company_id);
    }
  }, [todo, isEditModalOpen, signature, company]);

  useEffect(() => {
    if (company) {
      setSelectedCompanyId(company.id);
      setFormData((prev) => ({
        ...prev,
        company_id: company.id,
        company_name: company.name,
        company_type: company.type,
      }));
    }
  }, [company]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [companiesResponse, usersResponse] = await Promise.all([
        api.post<{ data: { data: Company[] } }>(
          "userService/getAllCompanyList",
          {
            signature,
          }
        ),
        api.post<{ data: { data: { allUsers: User[] } } }>(
          "userService/getActiveEmployeesListData",
          { signature }
        ),
      ]);
      setAllData({
        companies: companiesResponse.data.data.data,
        users: usersResponse.data.data.data.allUsers,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      showBasicAlert("Failed to fetch data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  interface Member {
    id: string;
    name: string;
    avatar?: string;
    email?: string;
    mobile_no?: string;
  }
  const SelectedMembersDisplay = ({
    members,
    onRemove,
  }: {
    members: Member[];
    onRemove: (id: string) => void;
  }) => {
    return (
      <div
        className="selected-members-container d-flex flex-wrap gap-2 mb-2 p-1 border border-secondary rounded"
        style={{
          minHeight: "40px",
          maxHeight: "40px",
          overflowY: "auto",
        }}
      >
        {members.length === 0 ? (
          <div className="text-center text-muted w-100">
            No members selected
          </div>
        ) : (
          members.map((member) => (
            <div
              key={member.id}
              className="member-tag d-flex align-items-center bg-light rounded-pill p-2"
              style={{
                maxWidth: "200px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {member.avatar ? (
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="rounded-circle me-2"
                  style={{
                    width: "15px",
                    height: "15px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle me-2"
                  style={{
                    width: "15px",
                    height: "15px",
                    backgroundColor: "#6c757d",
                    color: "white",
                    fontSize: "10px",
                  }}
                >
                  {member.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="me-2">{member.name}</span>
              <button
                onClick={() => onRemove(member.id)}
                className="btn btn-link p-0 text-muted"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
    );
  };

  const fetchComments = async () => {
    if (!viewTodo?.id) return;
    try {
      setIsCommentLoading(true);
      const response = await api.post("todoService/commentByTodoId", {
        signature,
        todo_id: viewTodo.id,
      });
      setComments(response.data.data.data || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
      showBasicAlert("Failed to fetch comments. Please try again.");
    } finally {
      setIsCommentLoading(false);
    }
  };

  useEffect(() => {
    const addModal = document.getElementById("add_todo");
    const viewModal = document.getElementById("view_todo");

    const handleAddModalShow = () => {
      setIsEditModalOpen(true);
      if (!todo) {
        setFormData({
          signature,
          title: "",
          description: "",
          priority: 0,
          stage: 0,
          status: 1,
          company_id: company?.id || selectedCompanyId || "",
          company_name:
            company?.name ||
            allData.companies.find((c) => c.id === selectedCompanyId)?.name ||
            "",
          company_type:
            company?.type ||
            allData.companies.find((c) => c.id === selectedCompanyId)?.type ||
            "",
          due_date: "",
          recurring_date: null,
          recurring_type: null,
          tags: [],
          attachments: [],
          assigned_members: [],
          team_leads: [],
          isActive: true,
        });
        setUiTodoType("Normal");
        setFilePreviews([]);
        setSelectedCompanyId(company?.id || selectedCompanyId || null);
      }
    };

    const handleViewModalShow = () => {
      setIsViewModalOpen(true);
      setComments([]);
      setNewComment("");
      setCommentFilePreviews([]);
    };

    const handleModalHide = () => {
      setIsEditModalOpen(false);
      setIsViewModalOpen(false);
      setFormData({
        signature,
        title: "",
        description: "",
        priority: 0,
        stage: 0,
        status: 1,
        company_id: company?.id || selectedCompanyId || "",
        company_name:
          company?.name ||
          allData.companies.find((c) => c.id === selectedCompanyId)?.name ||
          "",
        company_type:
          company?.type ||
          allData.companies.find((c) => c.id === selectedCompanyId)?.type ||
          "",
        due_date: "",
        recurring_date: null,
        recurring_type: null,
        tags: [],
        attachments: [],
        assigned_members: [],
        team_leads: [],
        isActive: true,
      });
      setUiTodoType("Normal");
      setFilePreviews([]);
      setComments([]);
      setNewComment("");
      setCommentFilePreviews([]);
      setDueDateError(null);
    };

    addModal?.addEventListener("show.bs.modal", handleAddModalShow);
    viewModal?.addEventListener("show.bs.modal", handleViewModalShow);
    addModal?.addEventListener("hide.bs.modal", handleModalHide);
    viewModal?.addEventListener("hide.bs.modal", handleModalHide);

    return () => {
      addModal?.removeEventListener("show.bs.modal", handleAddModalShow);
      viewModal?.removeEventListener("show.bs.modal", handleViewModalShow);
      addModal?.removeEventListener("hide.bs.modal", handleModalHide);
      viewModal?.removeEventListener("hide.bs.modal", handleModalHide);
    };
  }, [company, selectedCompanyId, allData.companies]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);

    if (
      filePreviews.length + files.length >
      Number(process.env.REACT_APP_MAX_ATTACHMENTS_TODO)
    ) {
      setFileUploadError(
        `You can upload a maximum of ${Number(
          process.env.REACT_APP_MAX_ATTACHMENTS_TODO
        )} attachments.`
      );
      return;
    } else {
      setFileUploadError(null);
    }

    const newPreviews = [...filePreviews];
    const newAttachments = Array.isArray(formData.attachments)
      ? [...formData.attachments]
      : [];

    for (const file of files) {
      if (
        !file.type.startsWith("image/") &&
        !file.type.startsWith("application/")
      ) {
        showBasicAlert("Only images and documents are allowed.");
        continue;
      }

      const uploadedFile = await uploadFileToCloudinary(file);
      if (uploadedFile) {
        const attachment = {
          name: file.name,
          url: uploadedFile.secure_url,
          type: file.type,
        };
        newPreviews.push(attachment);
        newAttachments.push(attachment);
      }
    }

    setFilePreviews(newPreviews);
    setFormData({ ...formData, attachments: newAttachments });
    e.target.value = "";
  };

  const handleCommentFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);
    const newPreviews = [...commentFilePreviews];

    for (const file of files) {
      if (
        !file.type.startsWith("image/") &&
        !file.type.startsWith("application/")
      ) {
        showBasicAlert("Only images and documents are allowed.");
        continue;
      }

      const uploadedFile = await uploadFileToCloudinary(file);
      if (uploadedFile) {
        newPreviews.push({
          name: file.name,
          url: uploadedFile.secure_url,
          type: file.type,
        });
      }
    }

    setCommentFilePreviews(newPreviews);
  };

  const uploadFileToCloudinary = async (file: File) => {
    const UPLOAD_PRESET: any = process.env.REACT_APP_UPLOAD_PRESET;
    const FOLDER_PATH = "cms/todo/";
    const CLOUDINARY_URL = `${process.env.REACT_APP_API_CLOUDNIRY_URL}/${process.env.REACT_APP_CLOUD_NAME}/upload`;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    formData.append("folder", FOLDER_PATH);

    try {
      const response = await axios.post(CLOUDINARY_URL, formData);
      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      showBasicAlert("Failed to upload file. Please try again.");
      return null;
    }
  };

  const removeAttachment = (index: number) => {
    const newPreviews = [...filePreviews];
    const newAttachments = [...formData.attachments];

    newPreviews.splice(index, 1);
    newAttachments.splice(index, 1);

    setFilePreviews(newPreviews);
    setFormData({ ...formData, attachments: newAttachments });

    if (
      newPreviews.length < Number(process.env.REACT_APP_MAX_ATTACHMENTS_TODO)
    ) {
      setFileUploadError(null);
    }
  };

  const removeCommentAttachment = (index: number) => {
    const newPreviews = [...commentFilePreviews];
    newPreviews.splice(index, 1);
    setCommentFilePreviews(newPreviews);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDueDateChange = (date: any) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, due_date: "" }));
      setDueDateError(null);
      return;
    }

    const selectedDate = dayjs(date);
    const today = dayjs().startOf("day");

    if (selectedDate.isBefore(today)) {
      setDueDateError("Due date cannot be in the past.");
      setFormData((prev) => ({ ...prev, due_date: "" }));
    } else {
      setDueDateError(null);
      const formattedDate = selectedDate.format("YYYY-MM-DD");
      setFormData((prev) => ({ ...prev, due_date: formattedDate }));
    }
  };

  const handleCompanySelect = (selectedOption: any) => {
    const companyId = selectedOption?.value || null;
    setSelectedCompanyId(companyId);
    if (companyId) {
      const selectedCompany = allData.companies.find((c) => c.id === companyId);
      if (selectedCompany) {
        setFormData((prev) => ({
          ...prev,
          company_id: selectedCompany.id,
          company_name: selectedCompany.name,
          company_type: selectedCompany.type,
        }));
      }
    }
  };

  const handleTagChange = (selected: any) => {
    setFormData((prev) => ({
      ...prev,
      tags: selected ? [selected.value] : [],
    }));
  };

  const handleDropdownSelect = (
    selectedId: string,
    field: "assigned_members" | "team_leads"
  ) => {
    const selectedUser = allData.users.find(
      (user) => user.EmpId === selectedId
    );
    if (selectedUser) {
      setFormData((prev) => {
        const currentList = prev[field];

        const updatedMember = {
          id: selectedUser.EmpId,
          name: selectedUser.Name,
          avatar: selectedUser.Image,
          email: selectedUser.Email,
          mobile_no:
            selectedUser.Official_Phone_code +
            selectedUser.Official_Phone_Number,
        };

        const exists = currentList.some(
          (item) => item.id === selectedUser.EmpId
        );

        let updatedList;
        if (exists) {
          updatedList = currentList.map((item) =>
            item.id === selectedUser.EmpId ? updatedMember : item
          );
        } else {
          updatedList = [...currentList, updatedMember];
        }

        return {
          ...prev,
          [field]: updatedList,
        };
      });
    }
  };

  const handleTodoTypeChange = (selected: any) => {
    const newTodoType = selected?.value || "Normal";
    setUiTodoType(newTodoType);
    if (newTodoType === "Normal") {
      setFormData((prev) => ({
        ...prev,
        recurring_date: null,
        recurring_type: null,
      }));
      setSelectedRecurringOption(null);
      setSelectedQuarter(null);
      setSelectedQuarterMonth(null);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() && commentFilePreviews.length === 0) return;

    setIsCommentLoading(true);

    const dataSend = {
      signature,
      todo_id: viewTodo?.id,
      company_id: viewTodo?.company_id,
      company_type: viewTodo?.company_type,
      comment: newComment,
      user_id: viewTodo?.reporter?.id || allData.users[0]?.EmpId,
      attachments: commentFilePreviews.map((file) => ({
        url: file.url,
        name: file.name,
        type: file.type,
      })),
    };

    try {
      const { data } = await api.post("todoService/addComments", dataSend);
      showBasicAlert(data.message[0].msg);
      setNewComment("");
      setCommentFilePreviews([]);
      await fetchComments();
    } catch (error: any) {
      showBasicAlert(error.data?.message?.[0]?.msg || "An error occurred");
    } finally {
      setIsCommentLoading(false);
    }
  };

  const handleEditComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editCommentText.trim() || !editingCommentId) return;

    setIsEditingComment(true);
    try {
      const response = await api.post("todoService/updateComments", {
        signature,
        id: editingCommentId,
        comment: editCommentText.trim(),
      });

      if (response.data.message[0].status) {
        showBasicAlert("Comment updated successfully");

        setComments(
          comments.map((comment) =>
            comment.id === editingCommentId
              ? { ...comment, comment: editCommentText.trim() }
              : comment
          )
        );

        setEditingCommentId(null);
        setEditCommentText("");
      }
    } catch (error: any) {
      showBasicAlert(
        error?.data?.message?.[0]?.msg || "Failed to update comment"
      );
    } finally {
      setIsEditingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await api.post("todoService/deleteComments", {
        signature,
        id: commentId,
      });

      if (response.data.message[0].status) {
        showBasicAlert("Comment deleted successfully");
        setComments(comments.filter((comment) => comment.id !== commentId));
      }
    } catch (error: any) {
      showBasicAlert(
        error?.data?.message?.[0]?.msg || "Failed to delete comment"
      );
    }
  };

  const startEditingComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.comment);
  };

  const cancelEditingComment = () => {
    setEditingCommentId(null);
    setEditCommentText("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        ...formData,
        signature,
        recurring_date:
          uiTodoType === "Recurring" ? formData.recurring_date : null,
        recurring_type:
          uiTodoType === "Recurring" ? formData.recurring_type : null,
      };

      let response;
      if (formData.id) {
        response = await api.post("todoService/updateTodos", payload);
        if (onSave) onSave();
      } else {
        response = await api.post("todoService/addTodos", payload);
        const newTodo = response.data.data;
        const newTodoTest = response?.data?.message[0]?.data;

        if (socketService.socket) {
          socketService.socket.emit("new_todo_created", {
            title: newTodoTest.title,
            message: `A new task "${newTodoTest.title}" has been created`,
            todo_id: newTodoTest.id,
            company_id: newTodoTest.company_id,
            company_type: newTodoTest.company_type,
            created_by: {
              id: newTodoTest.reporter?.id || "unknown",
              name: newTodoTest.reporter?.name || "Unknown User",
              image: getEmployeeImageUrl(
                allData.users,
                newTodoTest.reporter?.uid
              ),
            },
            assigned_members: newTodoTest.assigned_members,
            team_leads: newTodoTest.team_leads,
          });
        }

        setFormData({
          signature,
          title: "",
          description: "",
          priority: 0,
          stage: 0,
          status: 1,
          company_id: company?.id || selectedCompanyId || "",
          company_name:
            company?.name ||
            allData.companies.find((c) => c.id === selectedCompanyId)?.name ||
            "",
          company_type:
            company?.type ||
            allData.companies.find((c) => c.id === selectedCompanyId)?.type ||
            "",
          due_date: "",
          recurring_date: null,
          tags: [],
          attachments: [],
          assigned_members: [],
          team_leads: [],
          isActive: true,
        });
        setUiTodoType("Normal");
        setSelectedDueDateOption(null);
        setSelectedAssignedMembers([]);
        setSelectedTeamLeads([]);
        setFilePreviews([]);
        if (onSave) onSave(newTodo);
      }

      setIsFormSubmitSuccess(true);
      const dismissButton = document.querySelector(
        "#dismiss-modal-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }
    } catch (error: any) {
      showBasicAlert(error?.data?.message?.[0]?.msg || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body;
  };

  const getFilteredUsers = () => {
    if (!formData.company_id || !formData.company_type) return allData.users;

    return allData.users.filter((user) => {
      const userCompanyId =
        typeof user.CompanyId === "string"
          ? parseInt(user.CompanyId)
          : user.CompanyId;
      const formCompanyId =
        typeof formData.company_id === "string"
          ? parseInt(formData.company_id)
          : formData.company_id;

      return (
        userCompanyId === formCompanyId &&
        user.CompanyType === formData.company_type
      );
    });
  };

  return (
    <>
      <div
        className={`modal fade ${isEditModalOpen ? "show d-block" : ""}`}
        id="add_todo"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                {formData.id ? "Edit Task" : "Add New Task"}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                onClick={() => {
                  setSelectedAssignedMembers([]);
                  setSelectedTeamLeads([]);
                  setIsEditModalOpen(false);
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Company<span className="text-danger"> *</span>
                      </label>
                      <CommonSelect
                        className="select"
                        options={allData.companies.map((company) => ({
                          value: company.id,
                          label: `${company.name} - ${company.type}`,
                        }))}
                        defaultValue={
                          selectedCompanyId
                            ? {
                                value: selectedCompanyId,
                                label: `${
                                  allData.companies.find(
                                    (c) =>
                                      c.id === selectedCompanyId &&
                                      c.type === formData.company_type
                                  )?.name || formData.company_name
                                } - ${
                                  allData.companies.find(
                                    (c) =>
                                      c.id === selectedCompanyId &&
                                      c.type === formData.company_type
                                  )?.type || formData.company_type
                                }`,
                              }
                            : undefined
                        }
                        onChange={handleCompanySelect}
                        isDisabled={formData.company_id ? true : false}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Task Title<span className="text-danger"> *</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Task Type<span className="text-danger"> *</span>
                      </label>
                      <CommonSelect
                        className="select"
                        options={todoTypeOptions}
                        defaultValue={
                          uiTodoType
                            ? todoTypeOptions.find(
                                (opt) => opt.value === uiTodoType
                              )
                            : todoTypeOptions[0]
                        }
                        onChange={handleTodoTypeChange}
                        isDisabled={formData.id ? true : false}
                      />
                    </div>
                  </div>

                  {uiTodoType === "Recurring" && (
                    <>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Recurring Type
                            <span className="text-danger"> *</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={recurringType}
                            defaultValue={
                              formData.recurring_type
                                ? recurringType.find(
                                    (opt) =>
                                      opt.value === formData.recurring_type
                                  )
                                : undefined
                            }
                            onChange={handleRecurringTypeChange}
                            isDisabled={formData.id ? true : false}
                            placeHolder="Select"
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Recurring Date
                            <span className="text-danger"> *</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={
                              formData.recurring_type === "Weekly"
                                ? getWeekDays()
                                : getMonthDays()
                            }
                            defaultValue={selectedRecurringOption}
                            onChange={handleRecurringOptionSelect}
                            isDisabled={
                              !formData.recurring_type ||
                              (formData.id ? true : false)
                            }
                          />
                        </div>
                      </div>

                      {formData.recurring_type === "Weekly" && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Due Date
                              <span className="text-danger"> *</span>
                            </label>
                            <DatePicker
                              className="form-control datetimepicker"
                              format="DD-MM-YYYY"
                              value={
                                formData.due_date
                                  ? dayjs(formData.due_date)
                                  : null
                              }
                              getPopupContainer={getModalContainer}
                              placeholder="DD-MM-YYYY"
                              onChange={handleDueDateChange}
                              disabledDate={(current) =>
                                current && current < dayjs().startOf("day")
                              }
                            />
                            {dueDateError && (
                              <div className="text-danger mt-1">
                                {dueDateError}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {formData.recurring_type === "Monthly" && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Due Date
                              <span className="text-danger"> *</span>
                            </label>
                            <CommonSelect
                              className="select"
                              options={getMonthDays()}
                              defaultValue={selectedDueDateOption}
                              onChange={handleDueDateMonthly}
                              isDisabled={formData.id ? true : false}
                            />
                          </div>
                        </div>
                      )}

                      {formData.recurring_type === "Quarterly" && (
                        <>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Quarter
                                <span className="text-danger"> *</span>
                              </label>
                              <CommonSelect
                                className="select"
                                options={getQuarterMonths()}
                                defaultValue={selectedQuarter}
                                onChange={(selected: any) => {
                                  setSelectedQuarter(selected);
                                  handleQuarterSelect(selected);
                                }}
                                isDisabled={
                                  !formData.recurring_type ||
                                  (formData.id ? true : false)
                                }
                              />
                            </div>
                          </div>

                          {selectedQuarter && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Month in Quarter
                                  <span className="text-danger"> *</span>
                                </label>
                                <CommonSelect
                                  className="select"
                                  options={getQuarterMonthOptions(
                                    selectedQuarter.value
                                  )}
                                  defaultValue={selectedQuarterMonth}
                                  onChange={(selected: any) => {
                                    setSelectedQuarterMonth(selected);
                                    handleMonthInQuarterSelect(selected);
                                  }}
                                  isDisabled={!selectedQuarter}
                                />
                              </div>
                            </div>
                          )}

                          {selectedQuarterMonth && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Recurring Date
                                  <span className="text-danger"> *</span>
                                </label>
                                <CommonSelect
                                  className="select"
                                  options={getMonthDays()}
                                  defaultValue={selectedRecurringOption}
                                  onChange={handleRecurringOptionSelect}
                                  isDisabled={!selectedQuarterMonth}
                                />
                              </div>
                            </div>
                          )}

                          {formData.recurring_date && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Due Date
                                  <span className="text-danger"> *</span>
                                </label>
                                <DatePicker
                                  className="form-control datetimepicker"
                                  format="DD-MM"
                                  value={
                                    formData.due_date
                                      ? dayjs(formData.due_date)
                                      : null
                                  }
                                  getPopupContainer={getModalContainer}
                                  placeholder="DD-MM-YYYY"
                                  onChange={handleDueDateChange}
                                  onOpenChange={(open) => {
                                    if (open) {
                                      setTimeout(() => {
                                        const yearBtn = document.querySelector(
                                          ".ant-picker-header-view button:nth-child(2)"
                                        );
                                        if (yearBtn) {
                                          (
                                            yearBtn as HTMLElement
                                          ).style.display = "none";
                                        }

                                        const yearPanel =
                                          document.querySelector(
                                            ".ant-picker-year-panel"
                                          );
                                        if (yearPanel) {
                                          (
                                            yearPanel as HTMLElement
                                          ).style.display = "none";
                                        }
                                      }, 0);
                                    }
                                  }}
                                  disabledDate={(current) => {
                                    if (
                                      formData.recurring_type === "Quarterly" ||
                                      formData.recurring_type === "Weekly"
                                    ) {
                                      const start = dayjs(
                                        formData.recurring_date
                                      ).startOf("day");
                                      const end = dayjs(
                                        formData.due_date
                                      ).endOf("day");

                                      return current < start || current > end;
                                    }

                                    return (
                                      current &&
                                      current < dayjs().startOf("day")
                                    );
                                  }}
                                />
                                {dueDateError && (
                                  <div className="text-danger mt-1">
                                    {dueDateError}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {formData.recurring_date && (
                        <div className="col-12">
                          <div className="alert alert-info">
                            {moment(formData.recurring_date).format(
                              "MMMM Do, YYYY"
                            )}
                          </div>
                        </div>
                      )}

                      {formData.id && (
                        <div className="col-12">
                          <button
                            type="button"
                            className={`btn ${
                              formData.isActive ? "btn-danger" : "btn-secondary"
                            }`}
                            data-bs-dismiss="modal"
                            onClick={handleStopRecurring}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <span className="spinner-border spinner-border-sm me-1" />
                            ) : formData.isActive ? (
                              "Stop Recurring"
                            ) : (
                              "Start Recurring"
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {uiTodoType === "Normal" && (
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Due Date<span className="text-danger"> *</span>
                        </label>
                        <DatePicker
                          className="form-control datetimepicker"
                          format="DD-MM-YYYY"
                          value={
                            formData.due_date ? dayjs(formData.due_date) : null
                          }
                          getPopupContainer={getModalContainer}
                          placeholder="DD-MM-YYYY"
                          onChange={handleDueDateChange}
                          disabledDate={(current) =>
                            current && current < dayjs().startOf("day")
                          }
                        />
                        {dueDateError && (
                          <div className="text-danger mt-1">{dueDateError}</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Priority<span className="text-danger"> *</span>
                      </label>
                      <CommonSelect
                        className="select"
                        options={priorityOptions}
                        defaultValue={
                          formData.priority !== undefined
                            ? priorityOptions.find(
                                (opt) =>
                                  parseInt(opt.value) === formData.priority
                              )
                            : undefined
                        }
                        onChange={(selected: any) =>
                          setFormData((prev) => ({
                            ...prev,
                            priority: parseInt(selected.value),
                          }))
                        }
                      />
                    </div>
                  </div>

                  {formData.id && (
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Stage<span className="text-danger"> *</span>
                        </label>
                        <CommonSelect
                          className="select"
                          options={stageOptions}
                          defaultValue={
                            formData.stage !== undefined
                              ? stageOptions.find(
                                  (opt) =>
                                    parseInt(opt.value) === formData.stage
                                )
                              : undefined
                          }
                          onChange={(selected: any) =>
                            setFormData((prev) => ({
                              ...prev,
                              stage: parseInt(selected.value),
                            }))
                          }
                        />
                      </div>
                    </div>
                  )}

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Tag</label>
                      <CommonSelect
                        className="select"
                        options={tagOptions}
                        defaultValue={
                          formData.tags.length > 0
                            ? tagOptions.find(
                                (opt) => opt.value === formData.tags[0]
                              )
                            : undefined
                        }
                        onChange={handleTagChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-12 d-flex align-items-start mb-3">
                    <div className="flex-grow-1 me-3">
                      <label>
                        Assigned Members<span className="text-danger"> *</span>
                      </label>
                      <SelectedMembersDisplay
                        members={formData.assigned_members as Member[]}
                        onRemove={(id: string) => {
                          const updated = formData.assigned_members.filter(
                            (m) => m.id !== id
                          );
                          setFormData((prev) => ({
                            ...prev,
                            assigned_members: updated,
                          }));
                          const updatedSelected =
                            selectedAssignedMembers.filter(
                              (m) => m.value !== id
                            );
                          setSelectedAssignedMembers(updatedSelected);
                        }}
                      />
                    </div>
                    <div>
                      <label>Add Member</label>
                      <div style={{ width: "200px" }}>
                        <ImageSelectMultiple
                          isMulti={true}
                          options={getFilteredUsers().map((user) => ({
                            label: user.Name,
                            value: user.EmpId,
                            imageUrl: getEmployeeImageUrl(
                              allData.users,
                              user.EmpId
                            ),
                          }))}
                          value={selectedAssignedMembers}
                          onChange={(selectedOptions: any[] | null) => {
                            const safeOptions = selectedOptions || [];
                            setSelectedAssignedMembers(safeOptions);
                            const updatedMembers = safeOptions.map((sel) => ({
                              id: sel.value,
                              name: sel.label,
                              avatar: sel.imageUrl,
                            }));
                            setFormData((prev) => ({
                              ...prev,
                              assigned_members: updatedMembers,
                            }));
                            if (selectedOptions && selectedOptions.length > 0) {
                              selectedOptions.forEach((selected) => {
                                handleDropdownSelect(
                                  selected.value,
                                  "assigned_members"
                                );
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12 d-flex align-items-start mb-3">
                    <div className="flex-grow-1 me-3">
                      <label>Followers</label>
                      <SelectedMembersDisplay
                        members={formData.team_leads as Member[]}
                        onRemove={(id: string) => {
                          const updated = formData.team_leads.filter(
                            (m) => m.id !== id
                          );
                          setFormData((prev) => ({
                            ...prev,
                            team_leads: updated,
                          }));
                          const updatedSelected = selectedTeamLeads.filter(
                            (m) => m.value !== id
                          );
                          setSelectedTeamLeads(updatedSelected);
                        }}
                      />
                    </div>
                    <div>
                      <label>Add Follower</label>
                      <div style={{ width: "200px" }}>
                        <ImageSelectMultiple
                          isMulti={true}
                          options={getFilteredUsers().map((user) => ({
                            label: user.Name,
                            value: user.EmpId,
                            imageUrl: getEmployeeImageUrl(
                              allData.users,
                              user.EmpId
                            ),
                          }))}
                          value={selectedTeamLeads}
                          onChange={(selectedOptions: any[] | null) => {
                            const safe = selectedOptions || [];
                            setSelectedTeamLeads(safe);
                            const updated = safe.map((s) => ({
                              id: s.value,
                              name: s.label,
                              avatar: s.imageUrl,
                            }));
                            setFormData((prev) => ({
                              ...prev,
                              team_leads: updated,
                            }));
                            if (selectedOptions && selectedOptions.length > 0) {
                              selectedOptions.forEach((selected) => {
                                handleDropdownSelect(
                                  selected.value,
                                  "team_leads"
                                );
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Upload Attachment (Upto{" "}
                        {process.env.REACT_APP_MAX_ATTACHMENTS_TODO})
                      </label>
                      <div className="bg-light rounded p-2">
                        <div className="profile-uploader border-bottom mb-2 pb-2">
                          <div className="drag-upload-btn btn btn-sm btn-white border px-3">
                            Select File
                            <input
                              type="file"
                              className="form-control image-sign"
                              multiple
                              onChange={handleFileChange}
                            />
                          </div>
                        </div>
                        {filePreviews.map((file, index) => (
                          <div
                            key={index}
                            className="d-flex align-items-center justify-content-between border-bottom mb-2 pb-2"
                          >
                            <div className="d-flex align-items-center">
                              {file.type.startsWith("image/") ? (
                                <img
                                  src={file.url}
                                  alt={file.name}
                                  className="img-thumbnail me-2"
                                  style={{ width: "50px", height: "50px" }}
                                />
                              ) : (
                                <div className="file-icon me-2">
                                  <i
                                    className="ti ti-file"
                                    style={{ fontSize: "24px" }}
                                  />
                                </div>
                              )}
                              <h6 className="fs-12 fw-medium me-1">
                                {file.name}
                              </h6>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-icon"
                              onClick={() => removeAttachment(index)}
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                        ))}
                        {fileUploadError && (
                          <p className="text-danger mt-2 mb-0">
                            {fileUploadError}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Description<span className="text-danger"> *</span>
                      </label>
                      {formData.id && todo?.reporter?.uid !== localStorage.getItem("userId") ? (
                        <div
                          className="form-control"
                          style={{
                            minHeight: "100px",
                            backgroundColor: "#e9ecef",
                            padding: "8px 12px",
                            overflowY: "auto",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: fallbackHTML(formData.description),
                          }}
                        />
                      ) : (
                        <DefaultEditor
                          value={formData.description}
                          onChange={(e: any) =>
                            setFormData((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setSelectedAssignedMembers([]);
                    setSelectedTeamLeads([]);
                    setIsEditModalOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary m-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : formData.id ? (
                    "Update Task"
                  ) : (
                    "Add Task"
                  )}
                </button>
                <button
                  id="dismiss-modal-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${isViewModalOpen ? "show d-block" : ""}`}
        id="view_todo"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h4 className="modal-title fw-bold">📝 Task Details</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                onClick={() => setIsViewModalOpen(false)}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <div
              className="modal-body"
              style={{ overflowY: "auto", maxHeight: "75vh" }}
            >
              {viewTodo ? (
                <>
                  <section className="task-view-section">
                    <h6 className="text-muted mb-3">📋 Basic Information</h6>
                    <div className="row gy-3">
                      <div className="col-md-6">
                        <div>
                          <div className="task-view-label">Title</div>
                          <div className="task-view-value">
                            {viewTodo.title}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div>
                          <div className="task-view-label">Company</div>
                          <div className="task-view-value">
                            {viewTodo.company_name} - {viewTodo.company_type}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div>
                          <div className="task-view-label">Task Type</div>
                          <div className="task-view-value">
                            {viewTodo.recurring_date ? "Recurring" : "Normal"}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div>
                          <div className="task-view-label">Due Date</div>
                          <div className="task-view-value">
                            {viewTodo.due_date
                              ? moment(viewTodo.due_date).format("DD-MM-YYYY")
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div>
                          <div className="task-view-label">Recurring Date</div>
                          <div className="task-view-value">
                            {viewTodo.recurring_date
                              ? moment(viewTodo.recurring_date).format(
                                  "DD-MM-YYYY"
                                )
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div>
                          <div className="task-view-label">Priority</div>
                          <span
                            className={`badge task-view-badge ${
                              viewTodo.priority === 0
                                ? "bg-danger"
                                : viewTodo.priority === 1
                                ? "bg-warning"
                                : "bg-success"
                            }`}
                          >
                            {priorityOptions.find(
                              (opt) => parseInt(opt.value) === viewTodo.priority
                            )?.label || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div>
                          <div className="task-view-label">Stage</div>
                          <span
                            className={`badge task-view-badge ${
                              viewTodo.stage === 0
                                ? "bg-secondary"
                                : viewTodo.stage === 1
                                ? "bg-primary"
                                : viewTodo.stage === 2
                                ? "bg-warning"
                                : "bg-success"
                            }`}
                          >
                            {stageOptions.find(
                              (opt) => parseInt(opt.value) === viewTodo.stage
                            )?.label || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div>
                          <div className="task-view-label">Status</div>
                          <span
                            className={`badge task-view-badge ${
                              viewTodo.status === 1
                                ? "bg-success"
                                : "bg-secondary"
                            }`}
                          >
                            {viewTodo.status === 1 ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div>
                          <div className="task-view-label">Tags</div>
                          <div className="task-view-value">
                            {viewTodo.tags?.length
                              ? viewTodo.tags.join(", ")
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="task-view-section">
                    <h6 className="text-muted mb-3">👥 Assigned Members</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {viewTodo.assigned_members.map((member, i) => (
                        <div
                          className="task-avatar"
                          title={member.name}
                          key={i}
                        >
                          {getEmployeeImageUrl(allData.users, member.id) ? (
                            <img
                              src={getEmployeeImageUrl(
                                allData.users,
                                member.id
                              )}
                              alt={member.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div className="bg-primary text-white d-flex align-items-center justify-content-center h-100 w-100 fw-bold">
                              {member.name?.charAt(0).toUpperCase() || "?"}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="task-view-section">
                    <h6 className="text-muted mb-3">📌 Followers</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {viewTodo.team_leads.map((lead, i) => (
                        <div className="task-avatar" title={lead.name} key={i}>
                          {getEmployeeImageUrl(allData.users, lead.id) ? (
                            <img
                              src={getEmployeeImageUrl(allData.users, lead.id)}
                              alt={lead.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div className="bg-success text-white d-flex align-items-center justify-content-center h-100 w-100 fw-bold">
                              {lead.name?.charAt(0).toUpperCase() || "?"}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="task-view-section">
                    <h6 className="text-muted mb-3">📎 Attachments</h6>
                    {viewTodo.attachments?.length > 0 ? (
                      <div className="bg-light p-2 rounded">
                        {viewTodo.attachments.map((file, i) => (
                          <div
                            key={i}
                            className="d-flex align-items-center justify-content-between task-file-item p-2 mb-2"
                          >
                            <div className="d-flex align-items-center gap-2">
                              {file.type?.startsWith("image/") ? (
                                <img
                                  src={file.url || file.fileUrl}
                                  alt={file.name}
                                  className="rounded"
                                  style={{
                                    width: 50,
                                    height: 50,
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <i className="ti ti-file fs-4 text-muted"></i>
                              )}
                              <span className="fw-medium">
                                {file.name || "Untitled"}
                              </span>
                            </div>
                            <a
                              href={file.url || file.fileUrl}
                              download
                              className="text-decoration-none"
                            >
                              <i
                                className="ti ti-download fs-5 text-primary"
                                title="Download"
                              ></i>
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No attachments</p>
                    )}
                  </section>

                  <section className="task-view-section">
                    <h6 className="text-muted mb-3">🗒️ Description</h6>
                    <div
                      className="task-view-value"
                      dangerouslySetInnerHTML={{
                        __html: fallbackHTML(viewTodo.description),
                      }}
                    />
                  </section>

                  <section className="task-view-section">
                    <h6 className="text-muted mb-3">
                      💬 Comments ({comments.length})
                    </h6>
                    <div
                      className="mb-3"
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      {isCommentLoading && !comments.length ? (
                        <div className="text-center py-3">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : comments.length > 0 ? (
                        comments.map((comment, i) => (
                          <div key={i} className="card mb-2">
                            <div className="card-body p-2">
                              <div className="d-flex gap-2">
                                <div
                                  className="task-avatar"
                                  style={{ width: 40, height: 40 }}
                                >
                                  {getEmployeeImageUrl(
                                    allData.users,
                                    comment.reporter.uid
                                  ) ? (
                                    <img
                                      src={getEmployeeImageUrl(
                                        allData.users,
                                        comment.reporter.uid
                                      )}
                                      alt={comment.reporter.name}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <div className="bg-primary text-white d-flex align-items-center justify-content-center h-100 w-100 fw-bold">
                                      {comment.reporter.name.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <strong>{comment.reporter.name}</strong>
                                    <div className="d-flex align-items-center gap-2">
                                      <small className="text-muted">
                                        {moment(comment.createdAt).fromNow()}
                                      </small>
                                      {comment.reporter.uid ===
                                        localStorage.getItem("userId") && (
                                        <>
                                          <button
                                            className="btn btn-sm btn-link text-muted p-0"
                                            onClick={() =>
                                              startEditingComment(comment)
                                            }
                                            disabled={isEditingComment}
                                            title="Edit"
                                          >
                                            <i className="ti ti-edit"></i>
                                          </button>
                                          <button
                                            className="btn btn-sm btn-link text-danger p-0"
                                            onClick={() =>
                                              handleDeleteComment(comment.id)
                                            }
                                            disabled={isEditingComment}
                                            title="Delete"
                                          >
                                            <i className="ti ti-trash"></i>
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {editingCommentId === comment.id ? (
                                    <form
                                      onSubmit={handleEditComment}
                                      className="mt-2"
                                    >
                                      <textarea
                                        className="form-control mb-2"
                                        rows={3}
                                        value={editCommentText}
                                        onChange={(e) =>
                                          setEditCommentText(e.target.value)
                                        }
                                        disabled={isEditingComment}
                                      />
                                      <div className="d-flex gap-2">
                                        <button
                                          type="submit"
                                          className="btn btn-primary btn-sm"
                                          disabled={
                                            isEditingComment ||
                                            !editCommentText.trim()
                                          }
                                        >
                                          {isEditingComment
                                            ? "Updating..."
                                            : "Update"}
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-secondary btn-sm"
                                          onClick={cancelEditingComment}
                                          disabled={isEditingComment}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </form>
                                  ) : (
                                    <p className="mb-1">{comment.comment}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted">No comments yet</p>
                      )}
                    </div>

                    {!editingCommentId && (
                      <form onSubmit={handleCommentSubmit}>
                        <textarea
                          className="form-control mb-2"
                          rows={3}
                          placeholder="Add a comment..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          disabled={isCommentLoading}
                        />
                        <div className="d-flex align-items-center gap-3 mb-2">
                          <button
                            type="submit"
                            className="btn btn-primary btn-sm"
                            disabled={
                              isCommentLoading ||
                              (!newComment.trim() &&
                                !commentFilePreviews.length)
                            }
                          >
                            {isCommentLoading ? "Posting..." : "Post"}
                          </button>
                        </div>
                      </form>
                    )}
                  </section>
                </>
              ) : (
                <p className="text-center text-muted">No task data available</p>
              )}
            </div>

            <div className="modal-footer border-top-0">
              <button
                className="btn btn-light"
                data-bs-dismiss="modal"
                onClick={() => setIsViewModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TodoModal;
